import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import Dashboard from './Dashboard.js';
import './UserProfileView.css';

const UserProfileView = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [userDetails, setUserDetails] = useState(null);
  const [userCompanies, setUserCompanies] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      // Fetch user details and companies in parallel
      const [userResponse, companiesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/auth/get-user-id/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/companies/company-users/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUserDetails(userData);
        
        // Fetch profile picture if available
        if (userData.profile_picture_url) {
          fetchProfileImage(userData.profile_picture_url, token);
        }
      } else {
        setError('Failed to fetch user details');
      }

      if (companiesResponse.ok) {
        const companiesData = await companiesResponse.json();
        setUserCompanies(companiesData);
      } else {
        console.warn('Failed to fetch user companies');
        setUserCompanies([]);
      }

    } catch (error) {
      setError('Failed to fetch user data');
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileImage = async (imageUrl, token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}${imageUrl}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const imageObjectUrl = URL.createObjectURL(blob);
        setProfileImage(imageObjectUrl);
      }
    } catch (error) {
      console.error('Failed to fetch profile image:', error);
    }
  };

  const getUserTypeBadge = (userType) => {
    const typeMap = {
      'platform_user': { label: 'Platform User', class: 'user-type-user' },
      'platform_company': { label: 'Platform Company', class: 'user-type-company' },
      'admin': { label: 'Admin', class: 'user-type-admin' }
    };
    return typeMap[userType] || { label: 'Unknown', class: 'user-type-default' };
  };

  const getStatusBadge = (isActive) => {
    return {
      label: isActive ? 'Active' : 'Inactive',
      class: isActive ? 'status-active' : 'status-inactive'
    };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSinceOnline = (dateString) => {
    const lastOnline = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - lastOnline);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  if (loading) {
    return (
      <Dashboard page="users">
        <div className="user-profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading user profile...</p>
        </div>
      </Dashboard>
    );
  }

  if (error || !userDetails) {
    return (
      <Dashboard page="users">
        <div className="user-profile-error">
          <h2>Error Loading Profile</h2>
          <p>{error || 'User not found'}</p>
          <button onClick={() => navigate('/admin-users')} className="back-btn">
            Back to Users List
          </button>
        </div>
      </Dashboard>
    );
  }

  const userType = getUserTypeBadge(userDetails.user_type);
  const status = getStatusBadge(userDetails.is_active);

  return (
    <Dashboard page="users">
      <div className="user-profile-container">
        {/* Header Section */}
        <div className="user-profile-header">
          <button onClick={() => navigate('/list-users')} className="back-button">
            ← Back to Users
          </button>
          <div className="header-content">
            <div className="user-avatar-section">
              <div className="user-avatar-container">
                {profileImage ? (
                  <img src={profileImage} alt={`${userDetails.first_name} ${userDetails.last_name}`} className="user-avatar" />
                ) : (
                  <div className="user-avatar-placeholder">
                    {userDetails.first_name?.[0]}{userDetails.last_name?.[0]}
                  </div>
                )}
              </div>
              <div className="user-verification-badges">
                {userDetails.email_verified && (
                  <span className="verification-badge verified">✓ Email Verified</span>
                )}
                {!userDetails.email_verified && (
                  <span className="verification-badge unverified">✗ Email Not Verified</span>
                )}
                {userDetails.first_time_login && (
                  <span className="verification-badge first-login">🆕 First Time User</span>
                )}
              </div>
            </div>
            
            <div className="user-info-section">
              <h1 className="user-name">{userDetails.first_name} {userDetails.last_name}</h1>
              <p className="user-email">{userDetails.email}</p>
              <div className="user-badges">
                <span className={`user-type-badge ${userType.class}`}>
                  {userType.label}
                </span>
                <span className={`status-badge ${status.class}`}>
                  {status.label}
                </span>
              </div>
            </div>

            <div className="user-stats-section">
              <div className="stat-item">
                <span className="stat-label">Last Online</span>
                <span className="stat-value">{getTimeSinceOnline(userDetails.last_time_online)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Member Since</span>
                <span className="stat-value">{new Date(userDetails.created_at).toLocaleDateString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Companies</span>
                <span className="stat-value">{userCompanies.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="profile-tabs">
          <button 
            className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile Details
          </button>
          <button 
            className={`tab-button ${activeTab === 'companies' ? 'active' : ''}`}
            onClick={() => setActiveTab('companies')}
          >
            Companies ({userCompanies.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            Activity
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'profile' && (
            <div className="profile-details-tab">
              <div className="details-grid">
                <div className="detail-card">
                  <h3>Personal Information</h3>
                  <div className="detail-row">
                    <span className="detail-label">Full Name:</span>
                    <span className="detail-value">{userDetails.first_name} {userDetails.last_name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{userDetails.email}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{userDetails.phone_number || 'Not provided'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Bio:</span>
                    <span className="detail-value">{userDetails.bio || 'No bio provided'}</span>
                  </div>
                </div>

                <div className="detail-card">
                  <h3>Account Information</h3>
                  <div className="detail-row">
                    <span className="detail-label">User ID:</span>
                    <span className="detail-value user-id">{userDetails.id}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">User Type:</span>
                    <span className={`detail-value user-type-badge ${userType.class}`}>
                      {userType.label}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span className={`detail-value status-badge ${status.class}`}>
                      {status.label}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email Verified:</span>
                    <span className={`detail-value ${userDetails.email_verified ? 'verified' : 'unverified'}`}>
                      {userDetails.email_verified ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>

                <div className="detail-card">
                  <h3>Timeline</h3>
                  <div className="detail-row">
                    <span className="detail-label">Created At:</span>
                    <span className="detail-value">{formatDate(userDetails.created_at)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Last Updated:</span>
                    <span className="detail-value">{formatDate(userDetails.updated_at)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Last Online:</span>
                    <span className="detail-value">{formatDate(userDetails.last_time_online)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">First Time Login:</span>
                    <span className="detail-value">{userDetails.first_time_login ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'companies' && (
            <div className="companies-tab">
              {userCompanies.length > 0 ? (
                <div className="companies-grid">
                  {userCompanies.map((company) => (
                    <div key={company.id} className="company-card">
                      <div className="company-header">
                        <div className="company-logo">
                          {company.images && company.images.length > 0 ? (
                            <img 
                              src={company.images.find(img => img.is_primary)?.image_url || company.images[0].image_url} 
                              alt={company.name} 
                              className="company-image"
                            />
                          ) : (
                            <div className="company-placeholder">{company.name[0]}</div>
                          )}
                        </div>
                        <div className="company-info">
                          <h4 className="company-name">{company.name}</h4>
                          <p className="company-industry">{company.industry_name}</p>
                        </div>
                      </div>
                      
                      <div className="company-details">
                        <p className="company-description">{company.description}</p>
                        
                        <div className="company-stats">
                          <div className="stat">
                            <span className="stat-label">Employees:</span>
                            <span className="stat-value">{company.num_of_employees}</span>
                          </div>
                          <div className="stat">
                            <span className="stat-label">Active Jobs:</span>
                            <span className="stat-value">{company.active_jobs}</span>
                          </div>
                        </div>

                        {company.address && company.address.length > 0 && (
                          <div className="company-address">
                            <strong>Address:</strong>
                            <p>{company.address[0].street_address}</p>
                            <p>{company.address[0].city}, {company.address[0].state} {company.address[0].postal_code}</p>
                            <p>{company.address[0].country}</p>
                          </div>
                        )}

                        {company.website && (
                          <div className="company-links">
                            <a href={company.website} target="_blank" rel="noopener noreferrer" className="company-link">
                              🌐 Website
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <h3>No Companies Found</h3>
                  <p>This user is not associated with any companies.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="activity-tab">
              <div className="activity-timeline">
                <div className="timeline-item">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <h4>Last Online</h4>
                    <p>{formatDate(userDetails.last_time_online)}</p>
                    <small>{getTimeSinceOnline(userDetails.last_time_online)}</small>
                  </div>
                </div>
                
                <div className="timeline-item">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <h4>Profile Updated</h4>
                    <p>{formatDate(userDetails.updated_at)}</p>
                  </div>
                </div>
                
                <div className="timeline-item">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <h4>Account Created</h4>
                    <p>{formatDate(userDetails.created_at)}</p>
                  </div>
                </div>
              </div>

              <div className="activity-stats">
                <h3>Activity Summary</h3>
                <div className="stats-grid">
                  <div className="stat-card">
                    <h4>Account Status</h4>
                    <p className={status.class}>{status.label}</p>
                  </div>
                  <div className="stat-card">
                    <h4>Email Status</h4>
                    <p className={userDetails.email_verified ? 'verified' : 'unverified'}>
                      {userDetails.email_verified ? 'Verified' : 'Not Verified'}
                    </p>
                  </div>
                  <div className="stat-card">
                    <h4>Login Status</h4>
                    <p>{userDetails.first_time_login ? 'First time user' : 'Returning user'}</p>
                  </div>
                  <div className="stat-card">
                    <h4>Companies</h4>
                    <p>{userCompanies.length} associated</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Dashboard>
  );
};

export default UserProfileView;